/* ============================================================================
   PREPARACIÓN PARA UNA AUDITORÍA BARS

   El Basic Aviation Risk Standard de la Flight Safety Foundation usa el modelo
   bowtie: **amenazas → controles → evento → medidas de recuperación**. Cada
   control lleva un «Safety Goal» que dice para qué existe.

   Estructura verificada contra el estándar publicado: amenazas 1.0 (controles
   comunes) a 12.0 (medevac), más las defensas 19.0 ante accidente.

   ══════════════════════════════════════════════════════════════════════════
   LO QUE ESTE MÓDULO HACE, Y SOBRE TODO LO QUE NO

   **NO dice si el operador cumple BARS.** Eso lo determina un auditor
   acreditado por la Flight Safety Foundation, con la Question Master List, en
   una auditoría presencial de dos días. Ninguna aplicación puede sustituirlo.

   **Sí dice qué evidencia puede aportar HeliCalc para cada control**, y —más
   importante— **cuáles NO puede evidenciar**. Un operador que llega a la
   auditoría creyendo que la aplicación cubre algo que no cubre está peor que
   uno que sabe dónde tiene el hueco.

   La honestidad de esta matriz es su único valor. Marcar un control como
   cubierto cuando la evidencia es parcial produce exactamente la sorpresa que
   se quiere evitar.
   ══════════════════════════════════════════════════════════════════════════

   ADVERTENCIA DE VERSIÓN: la estructura de amenazas y controles proviene de
   una versión publicada del estándar. **El estándar vigente es posterior.**
   Antes de una auditoría real hay que cotejar contra la versión que exija el
   cliente — los números de control y los umbrales cambian entre versiones.
   ========================================================================== */

export const PROCEDENCIA_BARS = {
  estandar: "Basic Aviation Risk Standard — Contracted Aircraft Operations",
  emisor: "Flight Safety Foundation",
  modelo: "Bowtie: amenazas → controles → evento → recuperación",
  versionConsultada: "v7 (May 2018), estructura pública",
  advertencia: "El estándar vigente es posterior. Cotejar contra la versión "
    + "que exija el cliente antes de la auditoría: los números de control y los "
    + "umbrales cambian entre versiones.",
  auditoria: "La determina un auditor acreditado FSF con la Question Master List.",
};

/**
 * Grado de evidencia que la aplicación puede aportar.
 *
 * Solo tres valores, y el del medio es el que más importa: «parcial» significa
 * que hay algo pero no alcanza, y presentarlo como completo sería el error.
 */
export const EVIDENCIA = {
  completa: {
    clave: "completa",
    etiqueta: "Evidencia completa",
    nota: "La aplicación produce el registro que el auditor pide.",
  },
  parcial: {
    clave: "parcial",
    etiqueta: "Evidencia parcial",
    nota: "La aplicación aporta parte. Hace falta documentación adicional.",
  },
  ninguna: {
    clave: "ninguna",
    etiqueta: "Fuera del alcance",
    nota: "La aplicación no puede evidenciar esto. Se demuestra con otros documentos.",
  },
};

/**
 * Las amenazas del modelo bowtie, con su enunciado.
 *
 * El enunciado importa: BARS describe cada amenaza como el ACCIDENTE que
 * resulta, no como la condición. «Combustible agotado» no es la amenaza; la
 * amenaza es que la aeronave haga un aterrizaje forzoso por agotamiento y eso
 * termine en accidente.
 */
export const AMENAZAS = [
  { id: "1.0", nombre: "Controles comunes", enunciado: "Aplican a todas las amenazas." },
  { id: "2.0", nombre: "Salida de pista", enunciado: "La aeronave sale de la pista al despegar o aterrizar y resulta en accidente." },
  { id: "3.0", nombre: "Agotamiento de combustible", enunciado: "La aeronave hace un aterrizaje forzoso o amaraje por agotamiento de combustible." },
  { id: "4.0", nombre: "Contaminación de combustible", enunciado: "Combustible contaminado causa pérdida de potencia y aterrizaje forzoso." },
  { id: "5.0", nombre: "Vuelo controlado contra el terreno (CFIT)", enunciado: "Una aeronave aeronavegable bajo control de la tripulación es volada contra el terreno o el agua." },
  { id: "6.0", nombre: "Pérdida de control en vuelo (LOC-I)", enunciado: "Acciones de la tripulación llevan la aeronave fuera de la envolvente normal." },
  { id: "7.0", nombre: "Carga incorrecta", enunciado: "Carga incorrecta de pasajeros o falta de conciencia de seguridad resulta en accidente." },
  { id: "8.0", nombre: "Colisión en tierra", enunciado: "La aeronave y un objeto colisionan en tierra." },
  { id: "9.0", nombre: "Colisión en el aire", enunciado: "La aeronave y un objeto colisionan en el aire." },
  { id: "10.0", nombre: "Falla estructural o mecánica", enunciado: "Una falla estructural o mecánica resulta en pérdida de control." },
  { id: "11.0", nombre: "Meteorología", enunciado: "Las condiciones meteorológicas fuerzan a la aeronave a desviarse de su ruta planificada." },
  { id: "12.0", nombre: "Evacuación médica", enunciado: "Requisitos adicionales para vuelos medevac." },
  { id: "19.0", nombre: "Defensas ante accidente", enunciado: "Medidas de mitigación una vez ocurrido el accidente." },
];

/**
 * Los controles donde HeliCalc puede aportar algo.
 *
 * **Deliberadamente NO están los 100 y pico controles del estándar**: listar
 * todos con «fuera del alcance» daría una falsa sensación de cobertura y
 * escondería los pocos donde la aplicación sí sirve.
 *
 * Cada entrada dice el control, su objetivo según el estándar, qué produce
 * HeliCalc y qué falta. El campo `falta` es el más importante de la tabla.
 */
export const CONTROLES = [
  {
    id: "1.11",
    amenaza: "1.0",
    nombre: "Sistema de Gestión de Seguridad del operador",
    objetivo: "Asegurar que el SMS es eficaz para reunir y analizar información "
      + "de seguridad, gestionar el riesgo, dar garantía y asegurar mejora continua.",
    evidencia: "parcial",
    aporta: "Registro de reportes con canal de seguimiento, bitácora inmutable, "
      + "verificación de integridad y exportación auditable. Indicadores con "
      + "denominador y tendencia.",
    falta: "El Manual de SMS, la política de seguridad firmada, la estructura "
      + "organizacional, el programa de capacitación y las actas de las Juntas de Control.",
  },
  {
    id: "1.12",
    amenaza: "1.0",
    nombre: "Notificación de accidentes e incidentes",
    objetivo: "Asegurar que todo evento que afecte o pueda afectar la seguridad "
      + "se reporte apropiadamente.",
    evidencia: "parcial",
    aporta: "Captura del evento con fecha, tipo y descripción; bitácora de quién "
      + "lo trató y cuándo.",
    falta: "La evidencia de que se notificó AL CLIENTE contratante, que es lo que "
      + "el control exige. La aplicación registra el evento, no el aviso.",
  },
  {
    id: "1.13",
    amenaza: "1.0",
    nombre: "Evaluación de riesgo operacional",
    objetivo: "Asegurar que todos los riesgos de la operación se analizan, "
      + "minimizan y aceptan.",
    evidencia: "completa",
    aporta: "FRAT por vuelo con matriz de riesgo del operador, factores derivados "
      + "de datos verificables (topografía de la ruta, viento y meteorología del "
      + "METAR, reserva del combustible), mitigación y riesgo residual.",
    falta: null,
  },
  {
    id: "3.1",
    amenaza: "3.0",
    nombre: "Verificación de combustible",
    objetivo: "Asegurar que la aeronave despega con combustible suficiente para "
      + "conducir el vuelo con seguridad.",
    evidencia: "completa",
    aporta: "Cálculo de combustible requerido por tramo con la carta de consumo "
      + "de la aeronave, y comprobación contra el combustible a bordo.",
    falta: null,
  },
  {
    id: "3.2",
    amenaza: "3.0",
    nombre: "Datos meteorológicos para el plan de vuelo",
    objetivo: "Asegurar que se usan datos meteorológicos exactos al calcular "
      + "rutas y requisitos de combustible.",
    evidencia: "completa",
    aporta: "METAR crudo con hora de emisión, guardado con el plan. Viento por "
      + "nivel y nubes del modelo, con la procedencia declarada.",
    falta: null,
  },
  {
    id: "3.3",
    amenaza: "3.0",
    nombre: "Plan de vuelo",
    objetivo: "Asegurar que los vuelos se sujetan a planificación apropiada.",
    evidencia: "parcial",
    aporta: "Ruta con puntos, distancias, rumbos y tiempos.",
    falta: "La constancia de que el plan se PRESENTÓ ante el servicio de tránsito "
      + "aéreo o ante el responsable designado, que es lo que el control exige.",
  },
  {
    id: "3.5",
    amenaza: "3.0",
    nombre: "Plan de combustible VFR",
    objetivo: "Asegurar que se lleva combustible suficiente, incluidas las "
      + "reservas requeridas.",
    evidencia: "parcial",
    aporta: "Combustible de ruta y reserva calculados.",
    falta: "COMPROBAR que la reserva configurada cumple el mínimo BARS: 10% del "
      + "combustible de viaje MÁS 30 minutos fijos. Es más exigente que muchos "
      + "mínimos regulatorios.",
  },
  {
    id: "5.10",
    amenaza: "5.0",
    nombre: "Sistema de alerta de proximidad al terreno (TAWS)",
    objetivo: "Asegurar la detección de terreno y obstáculos para permitir "
      + "acción correctiva a tiempo.",
    evidencia: "ninguna",
    aporta: null,
    falta: "TAWS es equipamiento certificado a bordo. La representación de "
      + "terreno de HeliCalc es apoyo a la planificación y NO sustituye al equipo, "
      + "ni cuenta como evidencia de este control.",
  },
  {
    id: "7.1",
    amenaza: "7.0",
    nombre: "Peso de pasajeros",
    objetivo: "Asegurar que se usan pesos exactos de pasajeros en los cálculos "
      + "de carga.",
    evidencia: "parcial",
    aporta: "Carga por estación con peso individual.",
    falta: "BARS exige PESO REAL —no estándar— para todos los helicópteros. Hay "
      + "que demostrar que los pesos cargados son los pesados, no estimados.",
  },
  {
    id: "7.3",
    amenaza: "7.0",
    nombre: "Cálculos de carga y centrado",
    objetivo: "Asegurar carga exacta y segura dentro de los límites aprobados.",
    evidencia: "completa",
    aporta: "Peso y balance con envolvente del RFM, CG longitudinal y lateral, "
      + "y el resultado disponible para la cabina.",
    falta: null,
  },
  {
    id: "7.4",
    amenaza: "7.0",
    nombre: "Manifiesto",
    objetivo: "Asegurar que se levantan manifiestos exactos de pasajeros y se "
      + "conservan apropiadamente.",
    evidencia: "parcial",
    aporta: "Registro de las estaciones ocupadas y sus pesos.",
    falta: "El manifiesto exige NOMBRE COMPLETO de cada pasajero y que sea "
      + "accesible al personal de seguimiento de vuelo en todo momento.",
  },
  {
    id: "11.1",
    amenaza: "11.0",
    nombre: "Política de meteorología adversa",
    objetivo: "Establecer limitaciones meteorológicas consistentes con las "
      + "capacidades de la aeronave y los medios de rescate disponibles.",
    evidencia: "parcial",
    aporta: "Clasificación del METAR contra los mínimos del formulario FRAT, con "
      + "el motivo. Fenómenos significativos marcados.",
    falta: "La política escrita, acordada entre operador y cliente, que diga bajo "
      + "qué condiciones se restringe o detiene la operación.",
  },
  {
    id: "19.2",
    amenaza: "19.0",
    nombre: "Plan de Respuesta a Emergencias (ERP)",
    objetivo: "Asegurar que los procedimientos de respuesta ante emergencia "
      + "están actualizados y probados.",
    evidencia: "ninguna",
    aporta: null,
    falta: "El ERP, el documento puente con el cliente, y la constancia del "
      + "ejercicio ANUAL. Nada de esto lo produce la aplicación.",
  },
  {
    id: "19.5",
    amenaza: "19.0",
    nombre: "Seguimiento de vuelo",
    objetivo: "Asegurar que la posición de la aeronave se conoce en situaciones "
      + "normales y de emergencia.",
    evidencia: "ninguna",
    aporta: null,
    falta: "El seguimiento de vuelo BARS exige monitoreo EN TIERRA por personal "
      + "designado sin tareas secundarias, capaz de activar el ERP. El modo vuelo "
      + "de HeliCalc muestra la posición al piloto: es lo contrario de lo que "
      + "el control pide.",
  },
];

const texto = (v) => (typeof v === "string" ? v.trim() : "");

/** Los controles de una amenaza. */
export function controlesDe(amenazaId) {
  const id = texto(amenazaId);
  if (!id) return [];
  return CONTROLES.filter((c) => c.amenaza === id);
}

/**
 * Resumen de preparación.
 *
 * Devuelve los conteos y —lo que importa— la lista de huecos: los controles
 * donde la aplicación NO alcanza y hay que llevar otra cosa.
 */
export function resumenPreparacion(controles = CONTROLES) {
  const lista = Array.isArray(controles) ? controles : [];

  const porGrado = { completa: [], parcial: [], ninguna: [] };
  for (const c of lista) {
    const g = EVIDENCIA[c?.evidencia] ? c.evidencia : "ninguna";
    porGrado[g].push(c);
  }

  /* Los huecos son parcial + ninguna: en los dos casos hay que llevar algo
     más, y confundirlos es lo que produce la sorpresa en la auditoría. */
  const huecos = [...porGrado.parcial, ...porGrado.ninguna]
    .filter((c) => texto(c?.falta))
    .map((c) => ({ id: c.id, nombre: c.nombre, falta: c.falta, grado: c.evidencia }));

  return {
    total: lista.length,
    completa: porGrado.completa.length,
    parcial: porGrado.parcial.length,
    ninguna: porGrado.ninguna.length,
    huecos,
    /* NUNCA se devuelve un porcentaje de cumplimiento. Un «78% listo para
       BARS» es exactamente la cifra que un gerente lleva a una reunión y que
       no significa nada: los controles no pesan igual y la aplicación solo
       toca una fracción del estándar. */
    advertencia: "Esto no es un porcentaje de cumplimiento. La aplicación cubre "
      + "una fracción del estándar; el resto se demuestra con documentos y con "
      + "una auditoría presencial.",
  };
}

/**
 * Lo que hay que preparar antes de la auditoría, ordenado.
 *
 * Primero lo que la aplicación NO cubre en absoluto —es donde más trabajo hay
 * y donde más se tarda— y después lo parcial.
 */
export function listaDePreparacion(controles = CONTROLES) {
  const { huecos } = resumenPreparacion(controles);
  const orden = { ninguna: 0, parcial: 1 };
  return [...huecos].sort((a, b) => (orden[a.grado] ?? 9) - (orden[b.grado] ?? 9));
}

/**
 * Evidencia que la aplicación puede exportar para un control.
 *
 * Devuelve `null` cuando no hay nada que exportar: ofrecer una exportación
 * vacía haría creer que se cubrió algo.
 */
export function evidenciaPara(controlId) {
  const c = CONTROLES.find((x) => x.id === texto(controlId));
  if (!c || !texto(c.aporta)) return null;

  return {
    control: c.id,
    nombre: c.nombre,
    objetivo: c.objetivo,
    grado: c.evidencia,
    aporta: c.aporta,
    falta: c.falta,
    procedencia: PROCEDENCIA_BARS,
  };
}
